import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
import type { LDClient } from 'launchdarkly-js-client-sdk'

// Mirrors FEATURE_FLAGS_AVAILABLE in STO's feature-flag.js service.
// Add every LaunchDarkly flag key here before using it in the codebase.
// TypeScript will catch unknown keys at compile time via the FeatureFlagKey type.
export const FEATURE_FLAGS_AVAILABLE = [
	'sto-loyalty',
	'sto-item-images',
	'sto-account-manage',
	'sto-nested-mods',
	'sto-meta-pixel',
	'sto-referrals',

	'ew-olo-skip-order-settings',
	'sto-drop-off-points',
	'sto-dm-loyalty',
	'sto-enable-google-auto-complete',
	'sto-google-conversion-tracking',
	'sto-i4go-beta-url',
	'ew-olo-checkout-address-collection',
	'ew-olo-system-health-banner',
	'ew-olo-v2-migration',
	'ew-olo-v2-enabled',
	'ew-olo-google-pay',
] as const

export type FeatureFlagKey = (typeof FEATURE_FLAGS_AVAILABLE)[number]

interface FeatureFlagProviderProps {
	ldClient: LDClient | null
	children: React.ReactNode
}

export function FeatureFlagProvider({ ldClient, children }: FeatureFlagProviderProps) {
	if (!ldClient) {
		return <>{children}</>
	}

	// clientSideID is required by LDProvider's TS types but ignored at runtime when
	// ldClient is provided — the SDK uses the existing client and skips re-initialisation.
	return (
		<LDProvider
			clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID}
			ldClient={ldClient}
			reactOptions={{ useCamelCaseFlagKeys: false }}
		>
			{children}
		</LDProvider>
	)
}

// Typed wrapper so the rest of the codebase never imports the LD SDK directly.
// IMPORTANT: always pass the "safe off" / feature-disabled state as defaultValue — it is returned
// in every environment where LD credentials are absent, the flag is missing, or an error occurs.
// Mirrors STO's LaunchDarklyService.isEnabled(flagKey).
//
// useFlags() subscribes to flag change events so components re-render when LD pushes updates.
// ldClient.variation() is the authoritative read — it reads directly from the JS SDK's live
// in-memory cache, which is always correct after identify() resolves. useFlags() returns a React
// state snapshot that only updates on change events; for default-rule flags whose value is
// identical across contexts, no change event fires and the snapshot is never populated.
export function useFeatureFlag<T>(flagKey: FeatureFlagKey, defaultValue: T): T {
	// Intentionally unused return value — subscribes this component to flag change events so
	// it re-renders when LD pushes updates via the streaming connection.
	useFlags()
	const ldClient = useLDClient()

	try {
		if (ldClient) {
			return ldClient.variation(flagKey, defaultValue) as T
		}
		return defaultValue
	} catch {
		// If variation() throws for any reason, fall back to the safe default.
		return defaultValue
	}
}
